import PiloeerLogo from '@/assets/logo.svg?react';
import { EXTERNAL_LINKS } from '@/constants/external-links';
import { useUserRole } from '@/ui/@hooks/use-user-role';
import { APP_ROUTES } from '@/ui/application-routes';
import { isActiveRoute } from '@/utils/nav';
import { cn } from '@/utils/tailwind';
import { LifeBuoy } from 'lucide-react';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { getDashNavigationSections } from './dash-navigation';

export const SideBar: FunctionComponent = () => {
  const { role: userRole } = useUserRole();
  const { t } = useTranslation();
  const navSections = getDashNavigationSections({ role: userRole, t });

  return (
    <aside className='fixed inset-y-0 left-0 z-10 hidden w-64 flex-col border-r bg-popover md:flex'>
      <nav className='flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-3 py-4'>
        <Link
          to={APP_ROUTES.ROOT}
          className='group flex h-12 shrink-0 items-center gap-3 rounded-md px-3 text-lg font-semibold text-foreground md:text-base'
        >
          <span className='flex h-9 w-9 items-center justify-center rounded-md bg-foreground text-primary-foreground'>
            <PiloeerLogo className='h-5 w-5 transition-all group-hover:scale-110' />
          </span>
          <span className='truncate'>Piloteer</span>
        </Link>

        {navSections.map((section) => (
          <div
            key={
              section.label ?? section.items.map((item) => item.to).join('-')
            }
            className={cn('space-y-1', section.separator && 'border-t pt-4')}
          >
            {section.label && (
              <div className='px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
                {section.label}
              </div>
            )}
            {section.items.map((item) => {
              const isActive = isActiveRoute(item.to, item.activeRoutes);
              const Icon = item.icon;

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    'flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
                    isActive && 'bg-muted text-foreground'
                  )}
                  title={item.label}
                >
                  <Icon className='h-5 w-5 shrink-0' />
                  <span className='truncate'>{item.label}</span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
      <nav className='border-t px-3 py-4'>
        <a
          href={EXTERNAL_LINKS.SUPPORT_EMAIL}
          className='flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
        >
          <LifeBuoy className='h-4 w-4 shrink-0' />
          <span>{t('nav.support')}</span>
        </a>
      </nav>
    </aside>
  );
};
